import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AuthService } from '../../global-services/auth.service';
import {
    Organization,
    ReducedOrganization,
} from '../../user-account/user-organization.model';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { User, USER_GROUPS } from '../../user-account/user.model';
import { MyOrganizationsService } from '../../global-services/my-organization.service';
import { distinctUntilChanged, filter, finalize, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
    AMPLITUDE_EVENT_CATEGORIES,
    AMPLITUDE_EVENTS,
    AmplitudeService,
} from '../../global-services/amplitude.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { IndexedDbService } from '../../global-services/indexed-db.service';
import { IntercomService } from '../../global-services/intercom.service';
import { isNil } from 'lodash';

@Component({
    selector: 'account-drawer-component',
    templateUrl: './account-drawer-component.component.html',
    styleUrl: './account-drawer-component.component.scss',
    standalone: false
})
export class AccountDrawerComponentComponent implements OnInit {
    @Input() openAccountsDrawer: boolean;
    @Output() onOutsideClick = new EventEmitter<boolean>();

    ngUnsubscribe: Subject<any> = new Subject();
    filteredOrgList: Array<{
        id: number;
        name: string;
        master: boolean;
        role: string;
    }> = [];
    isOrganizationsDrawerOpen: boolean = false;
    previousOrg: ReducedOrganization;
    currentOrg: ReducedOrganization;
    fetchingOrganizationsOnScroll: boolean = false;
    fetchedAllOrganizations: boolean = false;
    organizationScrollChanged: Subject<boolean> = new Subject<boolean>();
    organizationSearchChanged: Subject<string> = new Subject<string>();
    pageNumber: number = 1;
    searchedOrgName: string = '';
    isMasterOrgSelected: boolean = false;
    showMasterOrgSection: boolean = true;
    showOrgsDropdown: boolean = false;
    currentOrganizationId: number;
    masterOrganization;
    currentCompleteOrg: Organization;
    masterOrgReduced: {
        role: string;
        name: any;
        id: any;
        beacon_count: any;
        master: boolean;
    };
    user: User;
    textChange = new EventEmitter<string>();
    deviceOs: string;

    @ViewChild('searchInput', { static: true }) input: ElementRef;

    constructor(
        private authService: AuthService,
        private myOrganizationsService: MyOrganizationsService,
        private amplitude: AmplitudeService,
        private deviceDetectorService: DeviceDetectorService,
        private ngxIndexedDBService: IndexedDbService,
        private intercom: IntercomService
    ) {
        this.authService.currentOrgId$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((orgID) => {
                this.currentOrganizationId = orgID;
            });

        this.currentOrg = this.authService.getCurrentReducedOrganization();

        this.masterOrganization = this.authService.getOrgByID(
            this.authService.getUser().mainOrganization
        );

        this.masterOrgReduced = {
            id: this.masterOrganization?.id,
            name: this.masterOrganization?.name,
            master: !this.masterOrganization?.parent,
            beacon_count: this.masterOrganization?.beacon_count,
            role: this.getReadableGroup(
                this.masterOrganization?.org_user_group
            ),
        };

        this.currentCompleteOrg = this.authService.getCurrentOrganization();
        this.user = this.authService.getUser();

        this.initOrgsDropDown();
    }

    ngOnInit() {
        this.organizationScrollChanged
            .pipe(debounceTime(100))
            .subscribe((event) => {
                this.pageNumber++;
                this.fetchingOrganizationsOnScroll = true;
                this.fetchOrgsInDropdown(true);
            });

        this.searchedOrgName = '';

        if (this.currentCompleteOrg.id === this.masterOrganization?.id) {
            this.isMasterOrgSelected = true;
        }

        this.organizationSearchChanged
            .pipe(debounceTime(500))
            .subscribe((event) => {
                this.searchedOrgName = event;
                this.pageNumber = 1;
                this.fetchedAllOrganizations = false;
                this.fetchOrgsInDropdown();
            });

        this.deviceOs = this.deviceDetectorService.getDeviceInfo().os;
    }

    // Fetch organizations to check the no of orgs
    initOrgsDropDown() {
        this.myOrganizationsService.getList(this.pageNumber, 20, {}, false, 'name')
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
            (res) => {
                if (!isNil(res?.totalCount) && res.totalCount > 1) {
                    this.showOrgsDropdown = true;
                    this.fetchOrgsInDropdown();
                } else {
                    this.showOrgsDropdown = false;
                }
            },
            (error) => {
                console.error('Error fetching organizations', error);
            }
        );
    };

    fetchOrgsInDropdown(onScroll: boolean = false) {
        const searchParams = {
            name__icontains: this.searchedOrgName.toLowerCase(),
        };
        this.myOrganizationsService
            .getList(this.pageNumber, 20, searchParams, false, 'name')
            .subscribe(
                (res) => {
                    if (this.searchedOrgName !== '') {
                        if (this.isMasterOrgSelected) {
                            // won't be altering the UI for this case
                        } else {
                            let isMasterOrgInSearchList = false;
                            for (const org of res.objects) {
                                isMasterOrgInSearchList =
                                    org.id === this.masterOrganization?.id;
                            }
                            this.showMasterOrgSection = isMasterOrgInSearchList
                                ? true
                                : false;
                        }
                    } else {
                        this.showMasterOrgSection = true;
                    }

                    if (!onScroll) {
                        this.filteredOrgList = [];
                    }

                    if (this.pageNumber === res.pageCount) {
                        this.fetchedAllOrganizations = true;
                    }
                    let isCurrentOrgInList = false;
                    for (const org of res.objects) {
                        // not pushing master organization to this.filteredOrgList as it is handled separately
                        if (org.id === this.masterOrganization?.id) {
                            continue;
                        }
                        if (org.id === this.currentOrganizationId) {
                            isCurrentOrgInList = true;
                            this.filteredOrgList.unshift({
                                id: org.id,
                                name:
                                    org.name.length <= 18
                                        ? org.name
                                        : org.name.substr(0, 18) + '...',
                                master: !org.parent,
                                role: this.getReadableGroup(org.org_user_group),
                            });
                        } else {
                            this.filteredOrgList.push({
                                id: org.id,
                                name:
                                    org.name.length <= 18
                                        ? org.name
                                        : org.name.substr(0, 18) + '...',
                                master: !org.parent,
                                role: this.getReadableGroup(org.org_user_group),
                            });
                        }
                    }
                    if (
                        !isCurrentOrgInList &&
                        !onScroll &&
                        this.currentCompleteOrg.id !==
                            this.masterOrganization?.id
                    ) {
                        this.filteredOrgList.unshift({
                            id: this.currentCompleteOrg.id,
                            name:
                                this.currentCompleteOrg.name.length <= 18
                                    ? this.currentCompleteOrg.name
                                    : this.currentCompleteOrg.name.substr(
                                          0,
                                          18
                                      ) + '...',
                            master: !this.currentCompleteOrg.parent,
                            role: this.getReadableGroup(
                                this.currentCompleteOrg.org_user_group
                            ),
                        });
                        this.authService.setOrganizationsList(res.objects);
                        this.authService.appendOrgToList(
                            this.currentCompleteOrg
                        );
                    } else if (!onScroll) {
                        this.authService.setOrganizationsList(res.objects);
                    } else {
                        this.authService.appendOrganizationsToList(res.objects);
                    }
                    this.fetchingOrganizationsOnScroll = false;
                },
                (error) => {
                    this.fetchingOrganizationsOnScroll = false;
                    console.log(error);
                }
            );
    }

    onOrgChanged(organization) {
        this.previousOrg = this.currentOrg;
        this.currentOrg = organization;
        this.isMasterOrgSelected =
            organization.id === this.masterOrganization?.id;
        if (organization.id !== this.authService.getCurrentOrgId()) {
            if (document.getElementById('beaconstac-search-organization')) {
                document.getElementById('beaconstac-search-organization')[
                    'value'
                ] = '';
            }
            this.authService.setCurrentOrgObjInLocalStorage(
                this.authService.getOrgByID(organization.id)
            );
            this.fetchedAllOrganizations = false;
            this.resetPageNumber();
            this.searchedOrgName = '';
        }
        this.isOrganizationsDrawerOpen = false;
        this.onOutsideClick.emit(false);
        this.currentCompleteOrg = this.authService.getCurrentOrganization();
        this.logAmplitudeEventForChangeOrg();
        window.location.reload();
    }

    searchOrganization(event) {
        this.organizationSearchChanged.next(event);
    }

    getSearchName(event) {
        fromEvent(this.input.nativeElement, 'keyup')
            .pipe(
                filter(Boolean),
                debounceTime(300),
                distinctUntilChanged(),
                tap((text) => {
                    this.textChange.emit(this.input.nativeElement.value);
                    this.searchOrganization(this.input.nativeElement.value);
                })
            )
            .subscribe();
    }

    getMoreData() {
        if (
            this.fetchedAllOrganizations ||
            this.fetchingOrganizationsOnScroll
        ) {
            return;
        }
        this.organizationScrollChanged.next(true);
    }

    onMasterOrgClick() {
        this.onOrgChanged(this.masterOrgReduced);
        this.isMasterOrgSelected = true;
    }
    hasAccessToMasterOrg() {
        return (
            this.authService.getUser().isSuperAdmin() ||
            this.authService.getUser().isSuperEditor() ||
            this.authService.getUser().isSuperViewer()
        );
    }

    getReadableGroup(user_group): string {
        switch (user_group) {
            case USER_GROUPS.Admin:
                return 'Admin';
            case USER_GROUPS.Owner:
                return 'Owner';
            case USER_GROUPS.ReadOnly:
                return 'Viewer';
            case USER_GROUPS.SandBox:
                return 'Editor';
            default:
                return '';
        }
    }

    logout() {
        this.ngxIndexedDBService
            .deleteDatabase()
            .pipe(
                finalize(() => {
                    this.amplitude.logEvent(
                        AMPLITUDE_EVENT_CATEGORIES.Usage,
                        AMPLITUDE_EVENTS.LOGOUT_INITIATED
                    );
                    this.authService.logout();
                }),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((res) => {
                console.info('Storage cleaned up', res);
            });
        this.intercom.shutdownIntercom();
    }

    openOrganizationDrawer() {
        this.isOrganizationsDrawerOpen = true;
    }

    handleOutsideClick() {
        // Emit 'false' to the parent component
        this.onOutsideClick.emit(false);
    }

    resetPageNumber() {
        this.pageNumber = 1;
    }

    handleRoutingToDashBoard() {
        this.amplitude.logEvent(
            AMPLITUDE_EVENT_CATEGORIES.Usage,
            AMPLITUDE_EVENTS.MYCARDS_GO_TO_DASHBOARD_CTA_CLICKED
        );
        window.open(environment.redirectToDashboard, '_blank');
    }

    logAmplitudeEventForChangeOrg() {
        this.amplitude.logEvent(
            AMPLITUDE_EVENT_CATEGORIES.Usage,
            AMPLITUDE_EVENTS.SWITCH_ORGANIZATION,
            {
                deviceOS: this.deviceOs,
                previousOrgId: this.previousOrg.id,
                currentOrgId: this.currentOrg.id,
            }
        );
    }
}
